import { RefreshCw } from 'lucide-react';
import { cn } from '@/utils/cn';

interface SyncBarProps {
  lastSyncedAt: string | null;
  pageCount: number;
  isSyncing: boolean;
  syncProgress: { current: number; total: number; itemTitle: string } | null;
  onSync: () => void;
  disabled?: boolean;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function SyncBar({
  lastSyncedAt,
  pageCount,
  isSyncing,
  syncProgress,
  onSync,
  disabled,
}: SyncBarProps) {
  return (
    <div className="flex items-center gap-3 py-3">
      <button
        onClick={onSync}
        disabled={disabled || isSyncing}
        aria-busy={isSyncing}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors',
          'bg-[var(--text-primary)] text-[var(--bg-primary)]',
          'hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
        {isSyncing ? 'Syncing...' : 'Sync Knowledge'}
      </button>

      {isSyncing && syncProgress ? (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <span>
              Processing {syncProgress.current} of {syncProgress.total} items
            </span>
          </div>
          <p className="text-xs text-[var(--text-tertiary)] truncate mt-0.5">
            {syncProgress.itemTitle}
          </p>
          <div className="mt-1 h-1 bg-[var(--border-primary)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--text-primary)] rounded-full transition-all duration-300"
              style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
          {lastSyncedAt && <span>Last synced: {timeAgo(lastSyncedAt)}</span>}
          {pageCount > 0 && (
            <>
              <span className="text-[var(--border-primary)]">·</span>
              <span>
                {pageCount} {pageCount === 1 ? 'page' : 'pages'}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
